/**
 * The ring an agent hears for an incoming call, made in the browser (no audio
 * file to ship): the familiar telephone double ring — two short bursts of a
 * 400 + 450 Hz tone, then a pause — repeated until answered.
 *
 * Browsers keep audio muted until the person has interacted with the page,
 * and an audio context created later can still start suspended. So one shared
 * context is unlocked on the agent's first click or key press anywhere in the
 * app (see unlockRingtoneOnFirstGesture), and resumed again on every ring.
 * Where audio is unavailable (tests, a browser that blocks it) this does
 * nothing — the ring still shows on screen and in the tab title.
 */

let shared: AudioContext | null = null;

function context(): AudioContext | null {
  if (shared) return shared;
  const AudioCtx: typeof AudioContext | undefined =
    typeof window !== 'undefined' ? window.AudioContext ?? (window as any).webkitAudioContext : undefined;
  if (!AudioCtx) return null;
  try {
    shared = new AudioCtx();
  } catch {
    shared = null;
  }
  return shared;
}

let unlockInstalled = false;

/** Unlock audio on the first click or key press, so a later ring is audible. */
export function unlockRingtoneOnFirstGesture(): void {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const unlock = () => {
    const ctx = context();
    void ctx?.resume?.().catch(() => {});
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
}

/** One ring cycle: ring 0.4s, gap 0.2s, ring 0.4s; the caller of this spaces cycles 3s apart. */
function ringOnce(ctx: AudioContext, at: number): void {
  for (const offset of [0, 0.6]) {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, at + offset);
    gain.gain.linearRampToValueAtTime(0.18, at + offset + 0.02);
    gain.gain.setValueAtTime(0.18, at + offset + 0.38);
    gain.gain.linearRampToValueAtTime(0, at + offset + 0.4);
    gain.connect(ctx.destination);
    for (const frequency of [400, 450]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      osc.connect(gain);
      osc.start(at + offset);
      osc.stop(at + offset + 0.42);
    }
  }
}

/** Start ringing; returns a function that stops it. */
export function startRingtone(): () => void {
  const ctx = context();
  if (!ctx) return () => {};

  let stopped = false;
  const ring = () => {
    if (stopped) return;
    void ctx.resume?.().catch(() => {});
    try {
      ringOnce(ctx, ctx.currentTime + 0.05);
    } catch {
      // The context was closed or is unavailable; the ring stays visual.
    }
  };
  ring();
  const timer = setInterval(ring, 3000);

  return () => {
    stopped = true;
    clearInterval(timer);
  };
}
